import { FormEvent, useState } from 'react';
import useSeo from '@/hooks/useSeo';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import useFormSubmit from '@/hooks/useFormSubmit';
import { site } from '@/mocks/site';
import { faqs } from '@/mocks/faqs';

const SITE_URL = 'https://captain2016.cn';
const FORM_ENDPOINT = 'https://readdy.ai/api/form/dahghproh653ivfvnuj0';

const topics = ['线索投稿', '内容更正', '商务合作', '其他问题'];

export default function Contact() {
  const { status, message, submit } = useFormSubmit();
  const [messageText, setMessageText] = useState('');

  useSeo({
    title: '联系我们 — 蝌蚪吃瓜线索投稿与内容更正',
    description:
      '联系蝌蚪吃瓜团队，提交娱乐事件线索、指出时间线错误或洽谈合作。建议附上信息出现的时间与来源链接，编辑会先核对再处理。',
    keywords: '蝌蚪吃瓜,联系我们,线索投稿,内容更正,商务合作',
    path: '/contact',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系蝌蚪吃瓜',
        url: `${SITE_URL}/contact`,
        description: '提交线索、内容更正与合作咨询。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '联系我们', item: `${SITE_URL}/contact` },
        ],
      },
    ],
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit(event.currentTarget, FORM_ENDPOINT);
  };

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="mx-auto w-full px-4 md:px-6">
          <Breadcrumbs items={[{ label: '联系我们' }]} />
          <div className="mt-5 max-w-3xl">
            <SectionHeading
              kicker="联系我们"
              title="有线索、有疑问，直接写给我们"
              description="无论是投稿一条新的时间线，还是指出已有节点的错误，我们都欢迎。附上信息出现的时间与来源，会帮助编辑更快完成核对。"
            />
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们会在收到后尽快查看。内容更正类信息会优先核对，通常三个工作日内给出是否采纳的回复。
            </p>

            <div className="mt-6 flex flex-col gap-4">
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg" />
                </span>
                <div>
                  <h3 className="font-label text-sm font-medium text-foreground-950">内容与投稿</h3>
                  <a href={`mailto:${site.email}`} className="mt-1 block text-sm text-primary-600 hover:underline">
                    {site.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-briefcase-line text-lg" />
                </span>
                <div>
                  <h3 className="font-label text-sm font-medium text-foreground-950">商务与合作</h3>
                  <a href={`mailto:${site.cooperateEmail}`} className="mt-1 block text-sm text-primary-600 hover:underline">
                    {site.cooperateEmail}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-time-line text-lg" />
                </span>
                <div>
                  <h3 className="font-label text-sm font-medium text-foreground-950">回复时间</h3>
                  <p className="mt-1 text-sm text-foreground-600">工作日 10:00 - 19:00，内容最后更新 {site.updateTime}</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">投稿前可以先看看</h2>
              <div className="mt-4 divide-y divide-background-200 border-y border-background-200">
                {faqs.slice(0, 3).map((item) => (
                  <details key={item.question} className="group py-4">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-label text-sm font-medium text-foreground-900 [&::-webkit-details-marker]:hidden">
                      {item.question}
                      <i className="ri-add-line text-lg text-primary-600 transition-transform group-open:rotate-45" />
                    </summary>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">提交信息</h2>
              <p className="mt-2 text-sm text-foreground-600">带 * 的为必填项，其余可留空。</p>

              <form
                id="kedou-contact-form"
                data-readdy-form
                action={FORM_ENDPOINT}
                method="post"
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-name" className="font-label text-sm text-foreground-800">
                      称呼 *
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      autoComplete="name"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-email" className="font-label text-sm text-foreground-800">
                      邮箱 *
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      autoComplete="email"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-topic" className="font-label text-sm text-foreground-800">
                    咨询类型 *
                  </label>
                  <div className="relative">
                    <select
                      id="contact-topic"
                      name="topic"
                      required
                      defaultValue={topics[0]}
                      className="w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                    >
                      {topics.map((topic) => (
                        <option key={topic} value={topic}>
                          {topic}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-foreground-500" />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="contact-message" className="font-label text-sm text-foreground-800">
                      详细说明 *
                    </label>
                    <span className="font-label text-xs text-foreground-500">{messageText.length}/500</span>
                  </div>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    value={messageText}
                    onChange={(event) => setMessageText(event.target.value.slice(0, 500))}
                    placeholder="请描述你了解的经过，并尽量附上信息出现的时间与来源链接。"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  />
                </div>

                <input
                  className="form-extra-note"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                  >
                    <i className="ri-send-plane-line" />
                    {status === 'submitting' ? '提交中…' : '提交信息'}
                  </button>
                  <p className="text-xs leading-relaxed text-foreground-500">
                    提交即表示你同意我们核对并使用该信息整理内容。
                  </p>
                </div>

                {status === 'success' && (
                  <div className="flex items-start gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-700" />
                    <p className="text-sm text-secondary-900">{message}</p>
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3">
                    <i className="ri-error-warning-line mt-0.5 text-primary-700" />
                    <p className="text-sm text-primary-900">{message}</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}